import * as React from 'react'
import * as XLSX from 'xlsx'
import axios from 'axios'
import { Box, Button, Chip, Container, Divider, Grid2, Paper, Stack, Typography } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import Navbar from '../setup/Navbar'
import TextFields from '../../BuiltInComponent/TextField'
import { useDispatch, useSelector } from 'react-redux'
import { addNewItemCompany, getAllItemCompanies, deleteItemCompany } from '../../features/GeneralManagement/itemCompanySlice'
import StickyHeadTable from '../../BuiltInComponent/TableWithPagination'
import Loading from '../../BuiltInComponent/Loading'

export default function ItemCompany() {
    const dispatch = useDispatch()
    const excelInputRef = React.useRef(null)
    const { itemCompanyies, isLoading } = useSelector((state) => state.itmCmp)
    const [formData, setFormData] = React.useState({ itmCmpDescription: '' })
    const [isImporting, setIsImporting] = React.useState(false)

    React.useEffect(() => { dispatch(getAllItemCompanies()) }, [dispatch])

    const onChange = (data) => {
        const key = Object.keys(data)[0]
        setFormData((current) => ({ ...current, [key]: data[key] }))
    }

    const onClick = async () => {
        if (!formData.itmCmpDescription.trim()) return window.alert('Please enter an item company name')
        const result = await dispatch(addNewItemCompany(formData))
        if (addNewItemCompany.fulfilled.match(result)) {
            setFormData({ itmCmpDescription: '' })
            await dispatch(getAllItemCompanies())
        } else window.alert(result.payload || 'Unable to add item company')
    }

    const downloadTemplate = () => {
        const worksheet = XLSX.utils.json_to_sheet([{ ItemCompany: 'National Foods' }])
        worksheet['!cols'] = [{ wch: 35 }]
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Item Companies')
        XLSX.writeFile(workbook, 'item_companies_import_template.xlsx')
    }

    const handleImport = async (event) => {
        const file = event.target.files && event.target.files[0]
        if (!file) return
        setIsImporting(true)
        try {
            const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' })
            const worksheet = workbook.Sheets[workbook.SheetNames[0]]
            const companies = XLSX.utils.sheet_to_json(worksheet, { defval: '' })
            if (!companies.length) throw new Error('Excel file mein koi item company record nahi hai')
            const response = await axios.post(global.SET_API_URL + 'itemCompany/importItemCompanies', { companies })
            await dispatch(getAllItemCompanies())
            window.alert(response.data.message)
        } catch (error) {
            window.alert(error.response?.data?.message || error.message || String(error))
        } finally {
            setIsImporting(false)
            event.target.value = ''
        }
    }

    const tableColumns = [
        { fieldName: 'itmCmpCode', cName: 'Item Company Code', textAlign: 'left', width: 100, status: 'en', dType: 'string' },
        { fieldName: 'itmCmpDescription', cName: 'Item Company Name', textAlign: 'left', width: 300, status: 'en', dType: 'string' },
        { fieldName: 'Edit', cName: 'Edit', textAlign: 'right', width: 20, status: 'dis', dType: 'string' },
        { fieldName: 'Delete', cName: 'Delete', textAlign: 'center', width: 20, status: 'en', dType: 'string' },
        { fieldName: 'Action', cName: 'Action', textAlign: 'right', width: 80, status: 'dis', dType: 'string' },
    ]

    const onDeleteClick = async (itmCmpCode) => {
        await dispatch(deleteItemCompany({ itmCmpCode }))
        await dispatch(getAllItemCompanies())
    }

    return <>
        <Navbar />
        <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: 'var(--web-theme-page-bg, #F5F7FB)', py: { xs: 2, md: 4 } }}>
            <Container maxWidth="xl">
                <Paper elevation={0} sx={{ mb: 3, p: { xs: 2.25, md: 3 }, borderRadius: 'var(--web-theme-radius, 12px)', color: 'var(--web-theme-topbar-text, #FFFFFF)', background: 'linear-gradient(135deg, var(--web-theme-topbar, #780202), var(--web-theme-primary, #A80707))' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{ width: 50, height: 50, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.15)' }}><BusinessRoundedIcon /></Box>
                            <Box><Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>Item Companies</Typography><Typography sx={{ opacity: 0.82, mt: 0.3 }}>Create and manage item companies from one place</Typography></Box>
                        </Stack>
                        <Chip label={`${(itemCompanyies || []).length} records`} sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'var(--web-theme-topbar-text, #FFFFFF)', fontWeight: 700, border: '1px solid rgba(255,255,255,0.22)' }} />
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 'var(--web-theme-radius, 12px)', borderColor: 'var(--web-theme-primary, #780202)', bgcolor: 'var(--web-theme-card-bg, #FFFFFF)' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={2}>
                        <Box><Typography fontWeight={750}>Bulk Excel tools</Typography><Typography variant="body2" color="text.secondary">Download the sample format, fill item company names, then import the file.</Typography></Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                            <Button startIcon={<DownloadRoundedIcon />} variant="outlined" onClick={downloadTemplate}>Download Template</Button>
                            <Button startIcon={<UploadFileRoundedIcon />} variant="contained" disabled={isImporting} onClick={() => excelInputRef.current.click()}>{isImporting ? 'Importing...' : 'Import Excel'}</Button>
                            <input ref={excelInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleImport} />
                        </Stack>
                    </Stack>
                </Paper>

                <Grid2 container spacing={3} alignItems="flex-start">
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2.25, md: 3 }, borderRadius: 'var(--web-theme-radius, 12px)', borderColor: 'rgba(0,0,0,0.1)', bgcolor: 'var(--web-theme-card-bg, #FFFFFF)', position: { md: 'sticky' }, top: { md: 16 } }}>
                            <Typography variant="h6" fontWeight={800}>Add Item Company</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>Enter a unique company name to create a new record.</Typography>
                            <TextFields label="Item Company Name" id="itmCmpDescription" name="itmCmpDescription" value={formData.itmCmpDescription} onChange={onChange} />
                            <Button fullWidth size="large" variant="contained" startIcon={<AddRoundedIcon />} disabled={isLoading} onClick={onClick} sx={{ mt: 2, py: 1.15, borderRadius: 2, fontWeight: 750 }}>Add Item Company</Button>
                            {isLoading && <Loading loadingText="Loading item companies" />}
                        </Paper>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 8 }}>
                        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 'var(--web-theme-radius, 12px)', borderColor: 'rgba(0,0,0,0.1)', overflow: 'hidden', bgcolor: 'var(--web-theme-card-bg, #FFFFFF)' }}>
                            <Box sx={{ px: { xs: 2, md: 3 }, py: 2.25 }}><Stack direction="row" justifyContent="space-between" alignItems="center"><Box><Typography variant="h6" fontWeight={800}>Item Companies List</Typography><Typography variant="body2" color="text.secondary">All active item companies currently available in the system.</Typography></Box><Chip size="small" color="primary" variant="outlined" label={(itemCompanyies || []).length} /></Stack></Box>
                            <Divider />
                            <Box sx={{ p: { xs: 1, md: 2 } }}><StickyHeadTable modernStyle tableColumns={tableColumns} tableData={itemCompanyies || []} onEditClick={() => {}} onDeleteClick={onDeleteClick} perPage={10} /></Box>
                        </Paper>
                    </Grid2>
                </Grid2>
            </Container>
        </Box>
    </>
}